import { FC, useCallback } from 'react';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import useDeclarations from 'hooks/useDeclarations';
import Notification from 'components/ui/base/notification/Notification';
import FormTextArea from 'components/ui/composed/formTextArea/FormTextArea';
import { getRequiredMessage } from 'views/declarations/utils/validation-utils';

const formValidation = Yup.object().shape({
    reason: Yup.string().required(getRequiredMessage('reason')),
});

interface Props {
    onCancel: () => void;
}

const InvalidateForm: FC<Props> = ({ onCancel }) => {
    const { invalidateDeclaration, error, isLoading, declaration } = useDeclarations();
    const { t } = useTranslation('customs_declarations');

    const formik = useFormik<{
        reason: string;
    }>({
        initialValues: {
            reason: '',
        },
        validationSchema: formValidation,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: (values) => {
            handleSubmit(values);
        },
        onReset: () => {
            onCancel();
        },
    });

    const handleInvalidate = useCallback(
        async (reason: string) => {
            if (!(declaration?.id && declaration?.declarationInternalType)) return;
            const res = await invalidateDeclaration(declaration.id, reason, declaration.declarationInternalType);
            if (res && !error && !isLoading) {
                onCancel();
                Notification({
                    type: 'success',
                    messageTitle: t('invalidate.invalidate-success-title'),
                    description: t('invalidate.invalidate-success-message'),
                    placement: 'top',
                });
                formik.resetForm();
            } else {
                Notification({
                    type: 'error',
                    messageTitle: t('invalidate.invalidate-error-title'),
                    description: t('invalidate.invalidate-error-message'),
                    placement: 'top',
                });
            }
        },
        [
            declaration?.id,
            onCancel,
            t,
            error,
            isLoading,
            invalidateDeclaration,
            declaration?.declarationInternalType,
            formik,
        ]
    );

    const handleSubmit = useCallback(
        async (values: { reason: string }) => {
            await handleInvalidate(values.reason);
        },
        [handleInvalidate]
    );

    return (
        <FormikProvider value={formik}>
            <form id="invalidateForm" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
                <FormTextArea
                    label="Reason"
                    required
                    fieldProps={formik.getFieldProps('reason')}
                    fieldMeta={formik.getFieldMeta('reason')}
                    placeholder="Enter invalidation reason"
                />
            </form>
        </FormikProvider>
    );
};

export default InvalidateForm;
